import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import {
  createChart,
  HistogramData,
  HistogramSeries,
  IChartApi,
  IRange,
  ISeriesApi,
  LineData,
  LineSeries,
  Time,
  UTCTimestamp,
  WhitespaceData
} from 'lightweight-charts';
import { useOnClickOutside } from 'usehooks-ts';

import { ElementNames, isTabletOrMobileDevice } from 'constants/app';
import { marketGraphsBranding } from 'constants/branding';
import { MARKET_GRAPH_LINE_CHART_SELECTION_BASE_URL } from 'constants/locations';
import {
  ChartSelections,
  DESKTOP_STATIC_ELEMENTS_HEIGHT,
  INTERVAL_1_DAY,
  INTERVAL_2_HOURS,
  INTERVAL_5_MIN,
  INTERVAL_15_SEC,
  IntervalsPeriodsMap,
  LINE_SERIES_CHANCE_PRICE_FORMAT,
  LINE_SERIES_ODDS_PRICE_FORMAT,
  LINE_SERIES_OPTIONS,
  MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME,
  MARKET_GRAPH_CHART_FILTERS,
  MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX,
  MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME,
  MARKET_GRAPH_SELECTION_PARAM_NAME,
  MARKET_GRAPH_TIME_FILTER_SESSION_STORAGE_NAME,
  MarketGraphChartDataGroupingPeriods,
  MarketGraphChartFilters,
  MIN_DESKTOP_WINDOW_HEIGHT,
  ONE_HOUR_IN_MILLISECONDS,
  ONE_MONTH_IN_MILLISECONDS,
  PeriodsIntervalsMap,
  PRICE_SCALE_OPTIONS,
  TOOLTIP_HEIGHT,
  TOOLTIP_MARGIN,
  TOOLTIP_WIDTH
} from 'constants/marketGraph';
import { useMarketUnits } from 'hooks/useMarketUnits';
import useOddsType from 'hooks/useOddsType';
import useTabulation from 'hooks/useTabulation';
import {
  getCurrency,
  getDisplayCurrencySymbol,
  getIndianNumberSystemEnabled,
  getIsAmericanDateFormatEnabled,
  getIsCurrencySymbolAfterAmount,
  getIsMarketGraphsDarkThemeEnabled,
  getIsMarketGraphsSimplifiedViewEnabled,
  getMarketGraphIsSmallScreenView,
  getReplaceCurrencySymbolForMatchedAndTooltips,
  getWindowHeight
} from 'redux/modules/appConfigs/selectors';
import { getElementHeightByName } from 'redux/modules/appSettings/selectors';
import {
  getMarketDefinitionInPlayStatus,
  getMarketDefinitionMarketUnit
} from 'redux/modules/marketDefinitions/selectors';
import {
  getMarketGraphChartDataPoints,
  getMarketGraphChartIsUpdated,
  getMarketGraphChartUpdatedPoints,
  getMarketGraphIsMarketBettingType,
  getMarketGraphMarketStartTime
} from 'redux/modules/marketGraph/selectors';
import { getPrecisionType } from 'redux/modules/user/selectors';
import {
  LineAndHistogramData,
  MarketGraphChartDataGroupingPeriod,
  MarketGraphChartFilter,
  MarketGraphChartSelection
} from 'types/marketGraph';
import { getFormatValueWithCurrency } from 'utils/currency';
import {
  concatExistAndPrevChartData,
  convertLineChartDataForSelection,
  getChartInterval,
  getChartOptions,
  getChartTimeValue,
  getLineAndHistogramData,
  getLineChartDataValue,
  groupChartDataByInterval
} from 'utils/marketGraph';
import { addZero } from 'utils/session';

import styles from './MarketGraphChartContent.module.scss';

/**
 * MarketGraphChartContent Component (description in `MARKET_GRAPH_CHART_README.md`)
 *
 * This component renders an interactive chart for visualizing market data, including odds/chance values
 * and trading volumes over time. It provides features for filtering, zooming, and exploring the data.
 *
 * Key features:
 * - Displays odds/chance values as a line chart
 * - Displays trading volumes as a histogram
 * - Supports different time interval filters (all time, day, hour)
 * - Provides zoom and pan functionality
 * - Shows tooltips with detailed information on hover
 * - Adapts to different screen sizes and device types
 * - Supports light/dark themes and simplified view options
 * - Handles real-time data updates
 *
 * The component maintains multiple data caches for different time periods to optimize
 * performance when switching between time filters or zoom levels. The main data
 * structure is periodsData.current which stores processed chart data for different
 * time resolutions (15sec, 5min, 2hours, 1day).
 *
 * Developer Modification Guide:
 * - Chart Appearance: Modify the getChartOptions function or LINE_SERIES_OPTIONS constant
 * - Data Processing: The groupChartDataByInterval and getLineAndHistogramData functions control how data is processed
 * - Time Intervals: Adjust INTERVAL_* constants to change the time resolution of different views
 * - Toolbar Controls: The toolbarItems array defines the available chart controls
 * - Performance: The data caching system (periodsData.current) is critical for performance - be careful when modifying
 * - Error Handling: The component has extensive error logging - maintain these when making changes
 *
 * When extending this component, consider:
 * 1. The complex state management using refs for performance-critical data
 * 2. The real-time update system that merges new data with existing data
 * 3. The responsive design that adapts to different screen sizes
 * 4. The keyboard shortcuts and accessibility features
 *
 * @param setIsChartError - Function to set error state if chart rendering fails
 * @returns React component that renders the interactive chart with controls
 */
export default function MarketGraphChartContent({
  setIsChartError
}: {
  setIsChartError: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    marketId = '',
    selectionId,
    handicap
  } = useParams<{ marketId: string; selectionId: string; handicap: string }>();
  const location = useLocation();

  const isSmallScreenView = useSelector(getMarketGraphIsSmallScreenView);
  const chartData = useSelector(getMarketGraphChartDataPoints);
  const americanDateFormatEnabled = useSelector(getIsAmericanDateFormatEnabled);
  const displayCurrencySymbol = useSelector(getDisplayCurrencySymbol);
  const currencySymbolAfterAmount = useSelector(getIsCurrencySymbolAfterAmount);
  const indianNumberSystemEnabled = useSelector(getIndianNumberSystemEnabled);
  const replaceCurrencySymbolForMatchedAndTooltips = useSelector(getReplaceCurrencySymbolForMatchedAndTooltips);
  const precisionType = useSelector(getPrecisionType);
  const currency = useSelector(getCurrency);
  const windowHeight = useSelector(getWindowHeight);
  const headerHeight = useSelector(getElementHeightByName(ElementNames.MARKET_GRAPH_HEADER));
  const totalMatchedBlockHeight = useSelector(getElementHeightByName(ElementNames.MARKET_GRAPH_TOTAL_MATCHED_BLOCK));
  const marketStartTime = useSelector(getMarketGraphMarketStartTime);
  const isChartDataUpdated = useSelector(getMarketGraphChartIsUpdated);
  const chartUpdatedPoints = useSelector(getMarketGraphChartUpdatedPoints);
  const isMarketInPlay = useSelector(getMarketDefinitionInPlayStatus(marketId));
  const isSimplifiedView = useSelector(getIsMarketGraphsSimplifiedViewEnabled);
  const isDarkTheme = useSelector(getIsMarketGraphsDarkThemeEnabled);
  const isLineMarket = useSelector(getMarketGraphIsMarketBettingType);
  const marketUnit = useSelector(getMarketDefinitionMarketUnit(marketId || ''));

  let defaultFilter = searchParams.get(MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME) as MarketGraphChartFilter | null;

  if (defaultFilter && defaultFilter === MarketGraphChartFilters.custom) {
    // need to set 'All time' if prev filter was 'custom'
    defaultFilter = MarketGraphChartFilters.all;
  }

  const [selectedFilter, setSelectedFilter] = useState<MarketGraphChartFilter>(
    defaultFilter || MarketGraphChartFilters.all
  );
  const [isOpenedDropdown, setIsOpenedDropdown] = useState(false);
  const [isChartZoomed, setIsChartZoomed] = useState(false);
  const [isToolbarPanLeftDisabled, setIsToolbarPanLeftDisabled] = useState(false);
  const [isToolbarPanRightDisabled, setIsToolbarPanRightDisabled] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownBtnRef = useRef<HTMLButtonElement>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const lineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const oddsLegendColorRef = useRef<HTMLDivElement>(null);
  const volumeLegendColorRef = useRef<HTMLDivElement>(null);
  const filterSelectionInProcess = useRef(false);
  const initialRange = useRef<IRange<Time> | null>(null);
  const isChartZoomedRef = useRef(isChartZoomed);
  const inPlayMarkerLineRef = useRef<HTMLDivElement | null>(null);
  const inPlayMarkerTextRef = useRef<HTMLParagraphElement | null>(null);
  const selectedFilterRef = useRef(selectedFilter);
  const currentInterval = useRef(0);
  const windowWidthRef = useRef(window.innerWidth);
  const allTimePeriod = useRef<MarketGraphChartDataGroupingPeriod>(MarketGraphChartDataGroupingPeriods.day1);
  const periodsData = useRef<
    Record<
      MarketGraphChartDataGroupingPeriod,
      { oddsData: (WhitespaceData | LineData)[]; volumeData: (WhitespaceData | HistogramData)[] }
    >
  >({
    [MarketGraphChartDataGroupingPeriods.sec15]: { oddsData: [], volumeData: [] },
    [MarketGraphChartDataGroupingPeriods.min5]: { oddsData: [], volumeData: [] },
    [MarketGraphChartDataGroupingPeriods.hour2]: { oddsData: [], volumeData: [] },
    [MarketGraphChartDataGroupingPeriods.day1]: { oddsData: [], volumeData: [] }
  });
  const isResetClicked = useRef(false);
  const isFirstRender = useRef(true);
  const isSimplifiedViewChanged = useRef(false); // needs to detect if chart was zoomed in subscribeVisibleTimeRangeChange
  const isToolbarPanLeftDisabledRef = useRef(isToolbarPanLeftDisabled);
  const isToolbarPanRightDisabledRef = useRef(isToolbarPanRightDisabled);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipDateRef = useRef<HTMLParagraphElement>(null);
  const tooltipOddsValueRef = useRef<HTMLParagraphElement>(null);
  const tooltipVolumeValueRef = useRef<HTMLParagraphElement>(null);
  const isScreenWidthResizing = useRef(false);
  const isDesktopSelectionChanged = useRef(false);

  useOnClickOutside(dropdownRef, () => setIsOpenedDropdown(false));
  const marketUnitTranslated = useMarketUnits(marketUnit || 'points');
  const { isAmericanOdds } = useOddsType();

  const isAmericanOddsEnabled = isAmericanOdds && !isLineMarket;
  let chartSelection =
    (searchParams.get(MARKET_GRAPH_SELECTION_PARAM_NAME) as MarketGraphChartSelection) || ChartSelections.odds;

  if (isAmericanOddsEnabled) {
    chartSelection = ChartSelections.chance;
  }

  if (isLineMarket) {
    chartSelection = ChartSelections.odds;
  }

  const minWindowHeight = windowHeight < MIN_DESKTOP_WINDOW_HEIGHT ? MIN_DESKTOP_WINDOW_HEIGHT : windowHeight;
  const isChanceView = chartSelection === ChartSelections.chance;
  const isMobile = isSmallScreenView || isTabletOrMobileDevice;
  const lastTimestamp = chartData?.length ? chartData[chartData.length - 1].timestamp : 0;

  const chartSelections = [
    { label: t('account.mybets.labels.odds'), value: ChartSelections.odds },
    { label: t('marketGraph.labels.chance'), value: ChartSelections.chance }
  ];

  /**
   * Updates the position of the in-play marker on the chart
   *
   * This function calculates the correct horizontal position for the in-play marker
   * based on the market start time. If the exact time coordinate cannot be found,
   * it finds the closest available time point or uses the last data point.
   */
  const updateInPlayMarkerPosition = () => {
    if (inPlayMarkerTextRef.current && chartRef.current && inPlayMarkerLineRef.current && marketStartTime) {
      const inPlayTime = (marketStartTime / 1000) as UTCTimestamp;
      let timeCoordinate = chartRef.current.timeScale().timeToCoordinate(inPlayTime);

      if (timeCoordinate === null) {
        const closestInPlayTimeTimestamp = periodsData.current[
          IntervalsPeriodsMap[currentInterval.current]
        ].oddsData.find(({ time }) => time > (inPlayTime as Time))?.time;

        if (closestInPlayTimeTimestamp) {
          timeCoordinate = chartRef.current.timeScale().timeToCoordinate(closestInPlayTimeTimestamp);
        } else {
          const oddsData = periodsData.current[IntervalsPeriodsMap[currentInterval.current]].oddsData;

          timeCoordinate = chartRef.current.timeScale().timeToCoordinate(oddsData[oddsData.length - 1].time);
        }
      }

      inPlayMarkerTextRef.current.style.left = `${timeCoordinate}px`;
      inPlayMarkerLineRef.current.style.left = `${timeCoordinate}px`;
    }
  };

  useEffect(() => {
    // This useEffect should be before chart was created to avoid extra changing chart options
    if (chartRef.current) {
      chartRef.current.applyOptions({
        rightPriceScale: {
          visible: !isSimplifiedView
        }
      });
    }
  }, [isSimplifiedView]);

  /**
   * Handles real-time chart data updates
   *
   * This effect processes new data points when they arrive, updating the chart
   * and maintaining cached data for different time periods. It:
   * 1. Groups new data points by the current interval
   * 2. Updates the chart with new points
   * 3. Stores the processed data in period-specific caches
   * 4. Updates the visible range if needed
   */
  useEffect(() => {
    if (
      chartUpdatedPoints.length &&
      volumeSeriesRef.current &&
      lineSeriesRef.current &&
      isChartDataUpdated &&
      currentInterval.current
    ) {
      const newData = groupChartDataByInterval({
        data: chartUpdatedPoints,
        interval: currentInterval.current
      });
      const newLineDataByCurrentInterval: (WhitespaceData | LineData)[] = [];
      const newHistogramDataByCurrentInterval: (WhitespaceData | HistogramData)[] = [];

      if (newData.length) {
        newData.forEach(newPoint => {
          const time = getChartTimeValue(newPoint.timestamp);
          const lineDataValue = getLineChartDataValue({ value: newPoint.value, selection: chartSelection });

          newLineDataByCurrentInterval.push({ time, value: lineDataValue });
          newHistogramDataByCurrentInterval.push({ time, value: newPoint.volume });

          try {
            lineSeriesRef.current?.update({ time, value: lineDataValue });
            volumeSeriesRef.current?.update({ time, value: newPoint.volume });
          } catch (error: unknown) {
            setIsChartError(true);
            // eslint-disable-next-line no-console
            console.log('update chart error', {
              error,
              periodsData: periodsData.current,
              newPoint,
              lineDataValue,
              chartData,
              chartUpdatedPoints,
              time,
              allTimePeriod: allTimePeriod.current,
              currentInterval: currentInterval.current
            });
          }
        });
      }

      // Determine the appropriate time period based on the full data range
      // This ensures we're using the optimal data resolution for the current time span
      const { period } = getChartInterval({
        startTimestamp: chartData[0].timestamp,
        endTimestamp: chartUpdatedPoints[chartUpdatedPoints.length - 1].timestamp
      });

      // Update the all-time period reference if needed
      if (period !== allTimePeriod.current) {
        allTimePeriod.current = period;
      }

      // Package the newly processed data points for the current interval
      const chartDataByCurrentInterval = {
        lineData: newLineDataByCurrentInterval,
        histogramData: newHistogramDataByCurrentInterval
      };

      // Determine which time resolution the current data matches
      // These flags help optimize data processing by reusing already processed data
      // when the current interval matches one of our standard periods
      const isDataForDay1 = IntervalsPeriodsMap[currentInterval.current] === MarketGraphChartDataGroupingPeriods.day1;
      const isDataForHour2 = IntervalsPeriodsMap[currentInterval.current] === MarketGraphChartDataGroupingPeriods.hour2;
      const isDataForMin5 = IntervalsPeriodsMap[currentInterval.current] === MarketGraphChartDataGroupingPeriods.min5;
      const isDataForSec15 = IntervalsPeriodsMap[currentInterval.current] === MarketGraphChartDataGroupingPeriods.sec15;

      // region store new points for all periods
      /**
       * Data Caching System for Chart Periods
       *
       * The code below updates periodsData.current, which is the central data cache for the chart.
       * This structure stores pre-processed data for four different time resolutions:
       * - day1: 1-day intervals for long time ranges (months)
       * - hour2: 2-hour intervals for medium time ranges (days to weeks)
       * - min5: 5-minute intervals for shorter time ranges (hours to a day)
       * - sec15: 15-second intervals for the shortest time ranges (minutes to an hour)
       *
       * For each resolution, we store both line data (odds/chance values) and histogram data (volumes).
       *
       * The update process:
       * 1. Determine which time resolution we're currently using
       * 2. Process new data points for all relevant resolutions
       * 3. Merge new data with existing cached data using concatExistAndPrevChartData
       * 4. Update the periodsData.current structure with the merged data
       *
       * This caching system allows for instant switching between different time filters
       * without having to reprocess the entire dataset each time.
       */
      if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.day1) {
        const day1Data = isDataForDay1
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_1_DAY
            });
        const hour2Data = isDataForDay1
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_2_HOURS
            });
        const min5Data = isDataForMin5
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_5_MIN
            });
        const sec15Data = isDataForSec15
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_15_SEC
            });

        const newConcatenatedDay1Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.day1].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.day1].volumeData,
          newLineChartData: day1Data.lineData,
          newHistogramChartData: day1Data.histogramData
        });
        const newConcatenatedHour2Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].volumeData,
          newLineChartData: hour2Data.lineData,
          newHistogramChartData: hour2Data.histogramData
        });
        const newConcatenatedMin5Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].volumeData,
          newLineChartData: min5Data.lineData,
          newHistogramChartData: min5Data.histogramData
        });
        const newConcatenatedSec15Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].volumeData,
          newLineChartData: sec15Data.lineData,
          newHistogramChartData: sec15Data.histogramData
        });

        periodsData.current = {
          [MarketGraphChartDataGroupingPeriods.day1]: {
            oddsData: newConcatenatedDay1Data.lineData,
            volumeData: newConcatenatedDay1Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.hour2]: {
            oddsData: newConcatenatedHour2Data.lineData,
            volumeData: newConcatenatedHour2Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: newConcatenatedMin5Data.lineData,
            volumeData: newConcatenatedMin5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: newConcatenatedSec15Data.lineData,
            volumeData: newConcatenatedSec15Data.histogramData
          }
        };
      } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.hour2) {
        const hour2Data = isDataForHour2
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_2_HOURS
            });
        const min5Data = isDataForMin5
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_5_MIN
            });
        const sec15Data = isDataForSec15
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_15_SEC
            });

        const newConcatenatedHour2Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].volumeData,
          newLineChartData: hour2Data.lineData,
          newHistogramChartData: hour2Data.histogramData
        });
        const newConcatenatedMin5Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].volumeData,
          newLineChartData: min5Data.lineData,
          newHistogramChartData: min5Data.histogramData
        });
        const newConcatenatedSec15Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].volumeData,
          newLineChartData: sec15Data.lineData,
          newHistogramChartData: sec15Data.histogramData
        });

        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.hour2]: {
            oddsData: newConcatenatedHour2Data.lineData,
            volumeData: newConcatenatedHour2Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: newConcatenatedMin5Data.lineData,
            volumeData: newConcatenatedMin5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: newConcatenatedSec15Data.lineData,
            volumeData: newConcatenatedSec15Data.histogramData
          }
        };
      } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.min5) {
        const min5Data = isDataForMin5
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_5_MIN
            });
        const sec15Data = isDataForSec15
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_15_SEC
            });

        const newConcatenatedMin5Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].volumeData,
          newLineChartData: min5Data.lineData,
          newHistogramChartData: min5Data.histogramData
        });
        const newConcatenatedSec15Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].volumeData,
          newLineChartData: sec15Data.lineData,
          newHistogramChartData: sec15Data.histogramData
        });

        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: newConcatenatedMin5Data.lineData,
            volumeData: newConcatenatedMin5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: newConcatenatedSec15Data.lineData,
            volumeData: newConcatenatedSec15Data.histogramData
          }
        };
      } else {
        const sec15Data = isDataForSec15
          ? chartDataByCurrentInterval
          : getLineAndHistogramData({
              chartData: chartUpdatedPoints,
              chartSelection,
              interval: INTERVAL_15_SEC
            });

        const newConcatenatedSec15Data = concatExistAndPrevChartData({
          prevLineChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData,
          prevHistogramChartData: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].volumeData,
          newLineChartData: sec15Data.lineData,
          newHistogramChartData: sec15Data.histogramData
        });

        periodsData.current = {
          ...periodsData.current,
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: newConcatenatedSec15Data.lineData,
            volumeData: newConcatenatedSec15Data.histogramData
          }
        };
      }

      // endregion store new points for all periods

      const { oddsData } = periodsData.current[allTimePeriod.current];

      initialRange.current = {
        ...initialRange.current,
        to: oddsData[oddsData.length - 1].time
      } as IRange<Time>;
    }
  }, [chartUpdatedPoints]);

  /**
   * Initializes and configures the chart
   *
   * This effect creates the chart instance and configures all its components:
   * - Sets up line and histogram series
   * - Configures chart appearance based on theme settings
   * - Sets up tooltip functionality
   * - Initializes data caching for different time periods
   * - Configures time scale and visible range
   * - Sets up event listeners for resize and time range changes
   */
  useEffect(() => {
    if (chartContainerRef.current && oddsLegendColorRef.current && volumeLegendColorRef.current) {
      const setInitialRange = () => {
        if (chartRef.current) {
          const currentRange = chartRef.current.timeScale().getVisibleRange();

          if (currentRange) {
            initialRange.current = currentRange;
            windowWidthRef.current = window.innerWidth;
          }
        }
      };

      let timeout: ReturnType<typeof setTimeout> | null = null;

      /**
       * Handles window resize events
       *
       * This function ensures the chart properly adapts to window size changes by:
       * - Tracking when resizing starts and ends
       * - Resetting zoom state when width changes
       * - Resizing the chart to fit the new container dimensions
       * - Updating the in-play marker position
       * - Fitting content to the new dimensions
       */
      const handleResize = () => {
        isScreenWidthResizing.current = true;

        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }

        timeout = setTimeout(() => {
          isScreenWidthResizing.current = false;

          if (timeout) {
            clearTimeout(timeout);
          }

          timeout = null;
        }, 500);

        windowWidthRef.current = window.innerWidth;

        if (chartRef.current && chartContainerRef.current) {
          chartRef.current.resize(chartContainerRef.current.clientWidth, chartContainerRef.current.clientHeight);

          updateInPlayMarkerPosition();

          const visibleRange = chartRef.current.timeScale().getVisibleRange();

          if (visibleRange) {
            chartRef.current.timeScale().setVisibleRange({
              from: visibleRange.from,
              to: visibleRange.to
            });
          }
        }
      };

      let initialAllTimeInterval: number;
      let initialAllTimePeriod: MarketGraphChartDataGroupingPeriod;

      const { interval: chartDataBiggestInterval, period: chartDataBiggestPeriod } = getChartInterval({
        startTimestamp: chartData[0].timestamp,
        endTimestamp: chartData[chartData.length - 1].timestamp
      });

      if (defaultFilter === MarketGraphChartFilters.day && isTabletOrMobileDevice) {
        initialAllTimeInterval = INTERVAL_5_MIN;
        initialAllTimePeriod = MarketGraphChartDataGroupingPeriods.min5;
      } else if (defaultFilter === MarketGraphChartFilters.hour && isTabletOrMobileDevice) {
        initialAllTimeInterval = INTERVAL_15_SEC;
        initialAllTimePeriod = MarketGraphChartDataGroupingPeriods.sec15;
      } else {
        initialAllTimeInterval = chartDataBiggestInterval;
        initialAllTimePeriod = chartDataBiggestPeriod;
      }

      currentInterval.current = initialAllTimeInterval;
      allTimePeriod.current = initialAllTimePeriod;

      const { lineData, histogramData } = getLineAndHistogramData({
        chartData,
        chartSelection,
        interval: initialAllTimeInterval
      });

      const chart = createChart(
        chartContainerRef.current,
        getChartOptions({
          t,
          americanDateFormatEnabled,
          isSimplifiedView,
          isDarkTheme,
          width: chartContainerRef.current.clientWidth
        })
      );
      chartRef.current = chart;

      const oddsLegend = getComputedStyle(oddsLegendColorRef.current);
      const volumeLegend = getComputedStyle(volumeLegendColorRef.current);

      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: volumeLegend.backgroundColor,
        lastValueVisible: false,
        priceFormat: {
          type: 'custom',
          formatter: (amount: number) => {
            if (amount < 0) {
              return '';
            }

            const { formattedAmount } = getFormatValueWithCurrency({
              amount,
              settings: {
                isCheckIndian: true,
                alwaysFlexibleDisplayFormat: true,
                noRounding: true,
                convertAmountToNumber: true
              },
              t,
              displayCurrencySymbol,
              currency,
              currencySymbolAfterAmount,
              indianNumberSystemEnabled,
              replaceCurrencySymbolForMatchedAndTooltips,
              precisionType
            });

            return formattedAmount;
          }
        },
        priceLineVisible: false,
        priceScaleId: 'right' // set as an overlay by setting a blank priceScaleId
      });
      volumeSeries.priceScale().applyOptions(PRICE_SCALE_OPTIONS);

      try {
        volumeSeries.setData(histogramData);
        volumeSeriesRef.current = volumeSeries;
      } catch (error: unknown) {
        setIsChartError(true);
        // eslint-disable-next-line no-console
        console.log('initial set histogram data error', {
          error,
          histogramData,
          periodsData: periodsData.current,
          initialAllTimeInterval,
          initialAllTimePeriod,
          chartData,
          allTimePeriod: allTimePeriod.current,
          currentInterval: currentInterval.current
        });
      }

      const lineSeries = chart.addSeries(LineSeries, {
        color: oddsLegend.backgroundColor,
        ...LINE_SERIES_OPTIONS,
        priceFormat: isChanceView ? LINE_SERIES_CHANCE_PRICE_FORMAT : LINE_SERIES_ODDS_PRICE_FORMAT
      });

      try {
        lineSeries.setData(lineData);
        lineSeriesRef.current = lineSeries;
      } catch (error: unknown) {
        setIsChartError(true);
        // eslint-disable-next-line no-console
        console.log('initial set line data error', {
          error,
          lineData,
          periodsData: periodsData.current,
          initialAllTimeInterval,
          initialAllTimePeriod,
          chartData,
          allTimePeriod: allTimePeriod.current,
          currentInterval: currentInterval.current
        });
      }

      const timeScale = chart.timeScale();

      timeScale.fitContent();

      if (isTabletOrMobileDevice) {
        if (defaultFilter === MarketGraphChartFilters.day) {
          timeScale.setVisibleRange({
            from: ((lastTimestamp - INTERVAL_1_DAY) / 1000) as UTCTimestamp,
            to: (lastTimestamp / 1000) as UTCTimestamp
          });
        } else if (defaultFilter === MarketGraphChartFilters.hour) {
          timeScale.setVisibleRange({
            from: ((lastTimestamp - ONE_HOUR_IN_MILLISECONDS) / 1000) as UTCTimestamp,
            to: (lastTimestamp / 1000) as UTCTimestamp
          });
        }
      }

      if (
        tooltipRef.current &&
        tooltipDateRef.current &&
        tooltipOddsValueRef.current &&
        tooltipVolumeValueRef.current
      ) {
        // update tooltip
        chart.subscribeCrosshairMove(param => {
          if (
            param.point === undefined ||
            !param.time ||
            param.point.x < 0 ||
            param.point.x > chartContainerRef.current!.clientWidth ||
            param.point.y < 0 ||
            param.point.y > chartContainerRef.current!.clientHeight
          ) {
            tooltipRef.current!.style.display = 'none';
          } else {
            const date = new Date((param.time as number) * 1000);
            tooltipRef.current!.style.display = 'block';
            const oddsData = param.seriesData.get(lineSeries);
            const volumeData = param.seriesData.get(volumeSeries);

            if (oddsData) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const price = oddsData.value !== undefined ? oddsData.value : oddsData.close;
              const hours = date.getHours();
              const minutes = date.getMinutes();
              const seconds = date.getSeconds();

              tooltipDateRef.current!.innerText = `${date.getDate()} ${t(
                `dates.shortMonth.${date.getMonth()}`
              )}, ${addZero(hours)}${hours}:${addZero(minutes)}${minutes}:${addZero(seconds)}${seconds}`;
              tooltipOddsValueRef.current!.innerText = `${Number(Number(price).toFixed(2))}`;
              tooltipVolumeValueRef.current!.innerText = volumeData
                ? getFormatValueWithCurrency({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    amount: volumeData.value,
                    settings: {
                      isCheckIndian: true,
                      ignoreFlexibleDisplayFormat: true,
                      noRounding: true
                    },
                    t,
                    displayCurrencySymbol,
                    currency,
                    currencySymbolAfterAmount,
                    indianNumberSystemEnabled,
                    replaceCurrencySymbolForMatchedAndTooltips,
                    precisionType
                  }).formattedAmount
                : '–';

              const coordinate = lineSeries.priceToCoordinate(price);
              let shiftedCoordinate = param.point.x - 43;
              if (coordinate === null) {
                return;
              }
              shiftedCoordinate = Math.max(
                0,
                Math.min(chartContainerRef.current!.clientWidth - TOOLTIP_WIDTH, shiftedCoordinate)
              );
              const coordinateY =
                coordinate - TOOLTIP_HEIGHT - TOOLTIP_MARGIN > 0
                  ? coordinate - TOOLTIP_HEIGHT - TOOLTIP_MARGIN
                  : Math.max(
                      0,
                      Math.min(
                        chartContainerRef.current!.clientHeight - TOOLTIP_HEIGHT - TOOLTIP_MARGIN,
                        coordinate + TOOLTIP_MARGIN
                      )
                    );
              tooltipRef.current!.style.left = shiftedCoordinate + 'px';
              tooltipRef.current!.style.top = coordinateY + 'px';
            }
          }
        });
      }

      // region data-caching
      // Code below (with if-else blocks) needs for caching grouped data for different periods, to switch between them
      // while zooming without recalculations for each zoom. It's determine the biggest
      // period - initialAllTimePeriod ('All time' filter) and depends on it makes calculations for others if needed

      if (
        isTabletOrMobileDevice &&
        (defaultFilter === MarketGraphChartFilters.day || defaultFilter === MarketGraphChartFilters.hour)
      ) {
        const day1Data: LineAndHistogramData = { lineData: [], histogramData: [] };
        const hours2Data: LineAndHistogramData = { lineData: [], histogramData: [] };
        const min5Data: LineAndHistogramData = { lineData: [], histogramData: [] };
        const sec15Data: LineAndHistogramData = { lineData: [], histogramData: [] };

        if (chartDataBiggestPeriod === MarketGraphChartDataGroupingPeriods.day1) {
          const periodDay1Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_1_DAY
          });
          day1Data.lineData = periodDay1Data.lineData;
          day1Data.histogramData = periodDay1Data.histogramData;
        }

        if (
          chartDataBiggestPeriod === MarketGraphChartDataGroupingPeriods.day1 ||
          chartDataBiggestPeriod === MarketGraphChartDataGroupingPeriods.hour2
        ) {
          const periodHours2Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_2_HOURS
          });
          hours2Data.lineData = periodHours2Data.lineData;
          hours2Data.histogramData = periodHours2Data.histogramData;
        }

        if (
          chartDataBiggestPeriod === MarketGraphChartDataGroupingPeriods.day1 ||
          chartDataBiggestPeriod === MarketGraphChartDataGroupingPeriods.hour2 ||
          chartDataBiggestPeriod === MarketGraphChartDataGroupingPeriods.min5
        ) {
          if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.min5) {
            min5Data.lineData = lineData;
            min5Data.histogramData = histogramData;
          } else {
            const periodMin5Data = getLineAndHistogramData({
              chartData,
              chartSelection,
              interval: INTERVAL_5_MIN
            });
            min5Data.lineData = periodMin5Data.lineData;
            min5Data.histogramData = periodMin5Data.histogramData;
          }
        }

        if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.sec15) {
          sec15Data.lineData = lineData;
          sec15Data.histogramData = histogramData;
        } else {
          const periodSec15Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_15_SEC
          });
          sec15Data.lineData = periodSec15Data.lineData;
          sec15Data.histogramData = periodSec15Data.histogramData;
        }

        periodsData.current = {
          [MarketGraphChartDataGroupingPeriods.day1]: {
            oddsData: day1Data.lineData,
            volumeData: day1Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.hour2]: {
            oddsData: hours2Data.lineData,
            volumeData: hours2Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.min5]: {
            oddsData: min5Data.lineData,
            volumeData: min5Data.histogramData
          },
          [MarketGraphChartDataGroupingPeriods.sec15]: {
            oddsData: sec15Data.lineData,
            volumeData: sec15Data.histogramData
          }
        };
      } else {
        if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.day1) {
          const hours2Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_2_HOURS
          });
          const min5Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_5_MIN
          });
          const sec15Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_15_SEC
          });

          periodsData.current = {
            [MarketGraphChartDataGroupingPeriods.day1]: { oddsData: lineData, volumeData: histogramData },
            [MarketGraphChartDataGroupingPeriods.hour2]: {
              oddsData: hours2Data.lineData,
              volumeData: hours2Data.histogramData
            },
            [MarketGraphChartDataGroupingPeriods.min5]: {
              oddsData: min5Data.lineData,
              volumeData: min5Data.histogramData
            },
            [MarketGraphChartDataGroupingPeriods.sec15]: {
              oddsData: sec15Data.lineData,
              volumeData: sec15Data.histogramData
            }
          };
        } else if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.hour2) {
          const min5Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_5_MIN
          });
          const sec15Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_15_SEC
          });

          periodsData.current = {
            ...periodsData.current,
            [MarketGraphChartDataGroupingPeriods.hour2]: { oddsData: lineData, volumeData: histogramData },
            [MarketGraphChartDataGroupingPeriods.min5]: {
              oddsData: min5Data.lineData,
              volumeData: min5Data.histogramData
            },
            [MarketGraphChartDataGroupingPeriods.sec15]: {
              oddsData: sec15Data.lineData,
              volumeData: sec15Data.histogramData
            }
          };
        } else if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.min5) {
          const sec15Data = getLineAndHistogramData({
            chartData,
            chartSelection,
            interval: INTERVAL_15_SEC
          });

          periodsData.current = {
            ...periodsData.current,
            [MarketGraphChartDataGroupingPeriods.min5]: { oddsData: lineData, volumeData: histogramData },
            [MarketGraphChartDataGroupingPeriods.sec15]: {
              oddsData: sec15Data.lineData,
              volumeData: sec15Data.histogramData
            }
          };
        } else if (initialAllTimePeriod === MarketGraphChartDataGroupingPeriods.sec15) {
          periodsData.current = {
            ...periodsData.current,
            [MarketGraphChartDataGroupingPeriods.sec15]: { oddsData: lineData, volumeData: histogramData }
          };
        }
      }

      if (searchParams.has(MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME)) {
        searchParams.delete(MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME);

        setSearchParams(searchParams, { replace: true });
      }

      setTimeout(setInitialRange, 200);

      // endregion data-caching

      let prevBarSpacing: number | null = null;
      let prevPeriod = initialAllTimePeriod;

      /**
       * Sets up time range change subscription
       *
       * This delayed setup ensures the chart is fully rendered before subscribing.
       * The subscription handles:
       * - Enabling/disabling pan controls based on visible range limits
       * - Updating the in-play marker position when range changes
       * - Detecting and handling zoom state changes
       * - Automatically selecting appropriate data resolution based on zoom level
       * - Updating chart data when resolution changes
       */
      setTimeout(() => {
        timeScale.subscribeVisibleTimeRangeChange(range => {
          if (!initialRange.current || !range || window.innerWidth !== windowWidthRef.current) return;

          const { oddsData } = periodsData.current[IntervalsPeriodsMap[currentInterval.current]];

          if (range.from === oddsData[0].time) {
            if (!isToolbarPanLeftDisabledRef.current) {
              isToolbarPanLeftDisabledRef.current = true;
              setIsToolbarPanLeftDisabled(true);
            }
          } else {
            if (isToolbarPanLeftDisabledRef.current) {
              isToolbarPanLeftDisabledRef.current = false;
              setIsToolbarPanLeftDisabled(false);
            }
          }

          if (range.to === oddsData[oddsData.length - 1].time) {
            if (!isToolbarPanRightDisabledRef.current) {
              isToolbarPanRightDisabledRef.current = true;
              setIsToolbarPanRightDisabled(true);
            }
          } else {
            if (isToolbarPanRightDisabledRef.current) {
              isToolbarPanRightDisabledRef.current = false;
              setIsToolbarPanRightDisabled(false);
            }
          }

          updateInPlayMarkerPosition();

          const isSameRange =
            Math.round(initialRange.current.from as number) === Math.round(range.from as number) &&
            Math.round(initialRange.current.to as number) === Math.round(range.to as number);

          if (
            isChartZoomedRef.current !== !isSameRange &&
            !isSimplifiedViewChanged.current &&
            !isScreenWidthResizing.current
          ) {
            isChartZoomedRef.current = !isSameRange;
            setIsChartZoomed(!isSameRange);
          }

          if (isSimplifiedViewChanged.current) {
            isSimplifiedViewChanged.current = false;
          }

          if (range && !filterSelectionInProcess.current) {
            const currentBarSpacing = timeScale.options().barSpacing;
            const rangeDiffInMilliseconds = ((range.to as number) - (range.from as number)) * 1000;
            let newPeriod: MarketGraphChartDataGroupingPeriod;

            if (rangeDiffInMilliseconds > ONE_MONTH_IN_MILLISECONDS) {
              newPeriod = MarketGraphChartDataGroupingPeriods.day1;
            } else if (
              rangeDiffInMilliseconds > INTERVAL_1_DAY &&
              rangeDiffInMilliseconds <= ONE_MONTH_IN_MILLISECONDS
            ) {
              newPeriod = MarketGraphChartDataGroupingPeriods.hour2;
            } else if (
              rangeDiffInMilliseconds > ONE_HOUR_IN_MILLISECONDS &&
              rangeDiffInMilliseconds <= INTERVAL_1_DAY
            ) {
              newPeriod = MarketGraphChartDataGroupingPeriods.min5;
            } else {
              newPeriod = MarketGraphChartDataGroupingPeriods.sec15;
            }

            if (prevBarSpacing === null) {
              prevBarSpacing = currentBarSpacing;
              return;
            }

            if (currentBarSpacing !== prevBarSpacing) {
              prevBarSpacing = currentBarSpacing;

              if (
                selectedFilterRef.current !== MarketGraphChartFilters.custom &&
                !isResetClicked.current &&
                !isScreenWidthResizing.current &&
                !isDesktopSelectionChanged.current
              ) {
                selectedFilterRef.current = MarketGraphChartFilters.custom;
                setSelectedFilter(MarketGraphChartFilters.custom);
                sessionStorage.removeItem(MARKET_GRAPH_TIME_FILTER_SESSION_STORAGE_NAME);
              }

              if (isResetClicked.current) {
                setTimeout(() => {
                  isResetClicked.current = false;
                }, 100);
              }

              if (isDesktopSelectionChanged.current) {
                setTimeout(() => {
                  isDesktopSelectionChanged.current = false;
                }, 500);
              }
            }

            if (
              prevPeriod !== newPeriod &&
              !!periodsData.current[newPeriod].oddsData &&
              !isResetClicked.current &&
              !isDesktopSelectionChanged.current &&
              !isScreenWidthResizing.current
            ) {
              currentInterval.current = PeriodsIntervalsMap[newPeriod];
              prevPeriod = newPeriod;

              try {
                // Need to reset data before set the new one
                lineSeries.setData([]);
                volumeSeries.setData([]);

                lineSeries.setData(periodsData.current[newPeriod].oddsData);
                volumeSeries.setData(periodsData.current[newPeriod].volumeData);
              } catch (error: unknown) {
                setIsChartError(true);
                // eslint-disable-next-line no-console
                console.log('set line and histogram chart in zoom error', {
                  error,
                  periodsData: periodsData.current,
                  newPeriod,
                  chartData,
                  allTimePeriod: allTimePeriod.current,
                  currentInterval: currentInterval.current
                });
              }
            }
          }
        });
      }, 100);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        chart.remove();
      };
    }
  }, []);

  useEffect(() => {
    if (marketStartTime && chartRef.current && isMarketInPlay) {
      setTimeout(() => {
        updateInPlayMarkerPosition();
      }, 200);
    }
  }, [marketStartTime, isMarketInPlay]);

  useEffect(() => {
    // There is some delay in height recalculation when switching from mobile to desktop
    setTimeout(() => {
      if (chartRef.current && chartContainerRef.current) {
        chartRef.current?.applyOptions({
          height: chartContainerRef.current?.clientHeight
        });
      }
    }, 100);
  }, [isMobile, headerHeight, totalMatchedBlockHeight]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else if (chartContainerRef.current) {
      chartRef.current?.applyOptions({ width: chartContainerRef.current.clientWidth });
      isSimplifiedViewChanged.current = true;
    }
  }, [isSimplifiedView]);

  /**
   * Pans the chart horizontally in the specified direction
   *
   * This function shifts the visible range of the chart left or right
   * by adjusting the scroll position of the time scale.
   *
   * @param direction - The direction to pan ('left' or 'right')
   */
  const panChart = (direction: 'left' | 'right') => {
    if (!chartRef.current) return;
    const timeScale = chartRef.current.timeScale();
    const visibleRange = timeScale.getVisibleRange();
    const scrollPosition = timeScale.scrollPosition();

    if (visibleRange?.from !== undefined && visibleRange.to !== undefined) {
      const shift = direction === 'left' ? -10 : 10; // Shift by 10 index

      timeScale.scrollToPosition(scrollPosition + shift, false);
    }
  };

  /**
   * Zooms the chart in or out
   *
   * This function adjusts the bar spacing of the time scale to create
   * a zoom effect. Increasing spacing zooms in, decreasing spacing zooms out.
   *
   * @param zoomIn - Whether to zoom in (true) or out (false)
   */
  const zoomChart = (zoomIn: boolean) => {
    if (!chartRef.current) return;
    const timeScale = chartRef.current.timeScale();
    const currentSpacing = timeScale.options().barSpacing;
    timeScale.applyOptions({
      barSpacing: zoomIn ? currentSpacing * 1.2 : currentSpacing * 0.8 // Increase/decrease spacing
    });
  };

  /**
   * Resets the chart to its initial state
   *
   * This function:
   * - Restores the original visible range
   * - Resets to the appropriate time interval data
   * - Resets the filter selection to "All time"
   * - Fits content to the chart dimensions
   */
  const resetChart = () => {
    if (!chartRef.current || !initialRange.current || !lineSeriesRef.current || !volumeSeriesRef.current) return;
    const timeScale = chartRef.current.timeScale();
    isResetClicked.current = true;

    const currentPeriod = IntervalsPeriodsMap[currentInterval.current];

    // Reset pan (visible range)
    timeScale.setVisibleRange({
      from: initialRange.current.from,
      to: initialRange.current.to
    });
    if (allTimePeriod.current !== currentPeriod) {
      currentInterval.current = PeriodsIntervalsMap[allTimePeriod.current];
      // Need to reset data before set the new one
      lineSeriesRef.current.setData([]);
      volumeSeriesRef.current.setData([]);

      lineSeriesRef.current.setData(periodsData.current[allTimePeriod.current].oddsData);
      volumeSeriesRef.current.setData(periodsData.current[allTimePeriod.current].volumeData);
    }

    timeScale.fitContent();
    selectedFilterRef.current = MarketGraphChartFilters.all;
    setSelectedFilter(MarketGraphChartFilters.all);
    sessionStorage.removeItem(MARKET_GRAPH_TIME_FILTER_SESSION_STORAGE_NAME);
  };

  /**
   * Handles time filter selection changes
   *
   * This function updates the chart when a different time filter is selected:
   * - "All time" shows the entire data range
   * - "Day" shows the last 24 hours of data
   * - "Hour" shows the last hour of data
   * - "Custom" is set automatically when user manually zooms
   *
   * It selects the appropriate data resolution and updates the visible range.
   *
   * @param newFilter - The new filter to apply
   */
  const handleSelectFilter = (newFilter: MarketGraphChartFilter) => {
    if (selectedFilter !== newFilter && lineSeriesRef.current && volumeSeriesRef.current && chartRef.current) {
      filterSelectionInProcess.current = true;
      selectedFilterRef.current = newFilter;
      setSelectedFilter(newFilter);
      sessionStorage.setItem(MARKET_GRAPH_TIME_FILTER_SESSION_STORAGE_NAME, newFilter);

      let period: MarketGraphChartDataGroupingPeriod | undefined;

      if (newFilter === MarketGraphChartFilters.day) {
        if (periodsData.current.min5.oddsData.length) {
          period = MarketGraphChartDataGroupingPeriods.min5;
          currentInterval.current = INTERVAL_5_MIN;
        } else {
          period = MarketGraphChartDataGroupingPeriods.sec15;
          currentInterval.current = INTERVAL_15_SEC;
        }
      } else if (newFilter === MarketGraphChartFilters.hour) {
        currentInterval.current = INTERVAL_15_SEC;
        period = MarketGraphChartDataGroupingPeriods.sec15;
      } else {
        if (periodsData.current.day1.oddsData.length) {
          period = MarketGraphChartDataGroupingPeriods.day1;
          currentInterval.current = INTERVAL_1_DAY;
        } else if (periodsData.current.hour2.oddsData.length) {
          period = MarketGraphChartDataGroupingPeriods.hour2;
          currentInterval.current = INTERVAL_2_HOURS;
        } else if (periodsData.current.min5.oddsData.length) {
          period = MarketGraphChartDataGroupingPeriods.min5;
          currentInterval.current = INTERVAL_5_MIN;
        } else {
          period = MarketGraphChartDataGroupingPeriods.sec15;
          currentInterval.current = INTERVAL_15_SEC;
        }
      }

      if (period) {
        try {
          // Need to reset data before set the new one
          lineSeriesRef.current.setData([]);
          volumeSeriesRef.current.setData([]);

          lineSeriesRef.current.setData(periodsData.current[period].oddsData);
          volumeSeriesRef.current.setData(periodsData.current[period].volumeData);

          if (period === MarketGraphChartDataGroupingPeriods.min5) {
            chartRef.current.timeScale().setVisibleRange({
              from: ((lastTimestamp - INTERVAL_1_DAY) / 1000) as UTCTimestamp,
              to: (lastTimestamp / 1000) as UTCTimestamp
            });
          } else if (period === MarketGraphChartDataGroupingPeriods.sec15) {
            chartRef.current.timeScale().setVisibleRange({
              from: ((lastTimestamp - ONE_HOUR_IN_MILLISECONDS) / 1000) as UTCTimestamp,
              to: (lastTimestamp / 1000) as UTCTimestamp
            });
          }
        } catch (error: unknown) {
          setIsChartError(true);
          // eslint-disable-next-line no-console
          console.log('set data on select new filter error', {
            error,
            periodsData: periodsData.current,
            chartData,
            newFilter,
            currentInterval: currentInterval.current,
            allTimePeriod: allTimePeriod.current
          });
        }

        if (newFilter === MarketGraphChartFilters.all) {
          chartRef.current.timeScale().fitContent();
        }

        setTimeout(() => {
          filterSelectionInProcess.current = false;
        }, 100);
      }
    }
  };

  /**
   * Handles switching between odds and chance views
   *
   * This function manages the transition between different chart data representations:
   * - Updates URL parameters to persist the selection
   * - Converts existing data between odds and chance formats
   * - Updates chart formatting to match the selected view
   * - Updates cached data for all time periods
   *
   * The conversion between odds and chance is handled by transforming the existing
   * data points rather than fetching new data.
   *
   * @param newSelection - The new view selection (odds or chance)
   */
  const handleChangeDesktopSelection = (newSelection: MarketGraphChartSelection) => {
    if (newSelection !== chartSelection) {
      localStorage.setItem(MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME, newSelection);
      searchParams.set(MARKET_GRAPH_SELECTION_PARAM_NAME, newSelection);
      setSearchParams(searchParams, { replace: true });
      setIsOpenedDropdown(false);

      if (lineSeriesRef.current && volumeSeriesRef.current) {
        if (selectedFilter === MarketGraphChartFilters.all || selectedFilter === MarketGraphChartFilters.custom) {
          const prevInterval = currentInterval.current;
          const newInterval = PeriodsIntervalsMap[allTimePeriod.current];

          const newLineChartData = convertLineChartDataForSelection({
            data: periodsData.current[allTimePeriod.current].oddsData
          });
          currentInterval.current = PeriodsIntervalsMap[allTimePeriod.current];

          if (selectedFilter !== MarketGraphChartFilters.all) {
            selectedFilterRef.current = MarketGraphChartFilters.all;
            setSelectedFilter(MarketGraphChartFilters.all);
            sessionStorage.removeItem(MARKET_GRAPH_TIME_FILTER_SESSION_STORAGE_NAME);
          }

          resetChart();

          if (newSelection === ChartSelections.chance) {
            lineSeriesRef.current?.applyOptions({
              priceFormat:
                newSelection === ChartSelections.chance
                  ? LINE_SERIES_CHANCE_PRICE_FORMAT
                  : LINE_SERIES_ODDS_PRICE_FORMAT
            });
          }

          // region update cached data
          // Code below (with if-else blocks) needs for update cached grouped data for different periods with new data for
          // line on chart according to new selection (above in useEffect where chart creates there is data-caching block)
          if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.day1) {
            const newHours2LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].oddsData
            });
            const newMin5LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData
            });
            const newSec15LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
            });

            periodsData.current = {
              [MarketGraphChartDataGroupingPeriods.day1]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.day1],
                oddsData: newLineChartData
              },
              [MarketGraphChartDataGroupingPeriods.hour2]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.hour2],
                oddsData: newHours2LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.min5]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
                oddsData: newMin5LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.sec15]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
                oddsData: newSec15LineChartData
              }
            };
          } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.hour2) {
            const newMin5LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData
            });
            const newSec15LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
            });

            periodsData.current = {
              ...periodsData.current,
              [MarketGraphChartDataGroupingPeriods.hour2]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.hour2],
                oddsData: newLineChartData
              },
              [MarketGraphChartDataGroupingPeriods.min5]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
                oddsData: newMin5LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.sec15]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
                oddsData: newSec15LineChartData
              }
            };
          } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.min5) {
            const newSec15LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
            });

            periodsData.current = {
              ...periodsData.current,
              [MarketGraphChartDataGroupingPeriods.min5]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
                oddsData: newLineChartData
              },
              [MarketGraphChartDataGroupingPeriods.sec15]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
                oddsData: newSec15LineChartData
              }
            };
          } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.sec15) {
            periodsData.current = {
              ...periodsData.current,
              [MarketGraphChartDataGroupingPeriods.sec15]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
                oddsData: newLineChartData
              }
            };
          }
          // endregion update cached data

          try {
            isDesktopSelectionChanged.current = true;
            if (prevInterval !== newInterval) {
              // Need to reset data before set the new one
              lineSeriesRef.current.setData([]);
              volumeSeriesRef.current.setData([]);
              lineSeriesRef.current.setData(newLineChartData);
              volumeSeriesRef.current.setData(periodsData.current[allTimePeriod.current].volumeData);
            } else {
              // Need to reset data before set the new one
              lineSeriesRef.current.setData([]);
              lineSeriesRef.current.setData(newLineChartData);
            }
          } catch (error: unknown) {
            setIsChartError(true);
            // eslint-disable-next-line no-console
            console.log('set line data on change desktop selection error', {
              error,
              chartSelection,
              newSelection,
              newLineChartData,
              chartData,
              periodsData: periodsData.current,
              allTimePeriod: allTimePeriod.current,
              currentInterval: currentInterval.current
            });
          }
        } else {
          const currentGroupingPeriod =
            selectedFilter === MarketGraphChartFilters.day
              ? MarketGraphChartDataGroupingPeriods.min5
              : MarketGraphChartDataGroupingPeriods.sec15;
          const newLineChartData = convertLineChartDataForSelection({
            data: periodsData.current[currentGroupingPeriod].oddsData
          });

          if (newSelection === ChartSelections.chance) {
            lineSeriesRef.current?.applyOptions({
              priceFormat:
                newSelection === ChartSelections.chance
                  ? LINE_SERIES_CHANCE_PRICE_FORMAT
                  : LINE_SERIES_ODDS_PRICE_FORMAT
            });
          }

          // region update cached data
          // Code below (with if-else blocks) needs for update cached grouped data for different periods with new data for
          // line on chart according to new selection (above in useEffect where chart creates there is data-caching block)
          if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.day1) {
            const newDay1LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.day1].oddsData
            });
            const newHours2LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].oddsData
            });
            const newMin5LineChartData =
              currentGroupingPeriod === MarketGraphChartDataGroupingPeriods.min5
                ? newLineChartData
                : convertLineChartDataForSelection({
                    data: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData
                  });
            const newSec15LineChartData =
              currentGroupingPeriod === MarketGraphChartDataGroupingPeriods.sec15
                ? newLineChartData
                : convertLineChartDataForSelection({
                    data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
                  });

            periodsData.current = {
              [MarketGraphChartDataGroupingPeriods.day1]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.day1],
                oddsData: newDay1LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.hour2]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.hour2],
                oddsData: newHours2LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.min5]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
                oddsData: newMin5LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.sec15]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
                oddsData: newSec15LineChartData
              }
            };
          } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.hour2) {
            const newHours2LineChartData = convertLineChartDataForSelection({
              data: periodsData.current[MarketGraphChartDataGroupingPeriods.hour2].oddsData
            });
            const newMin5LineChartData =
              currentGroupingPeriod === MarketGraphChartDataGroupingPeriods.min5
                ? newLineChartData
                : convertLineChartDataForSelection({
                    data: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData
                  });
            const newSec15LineChartData =
              currentGroupingPeriod === MarketGraphChartDataGroupingPeriods.sec15
                ? newLineChartData
                : convertLineChartDataForSelection({
                    data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
                  });

            periodsData.current = {
              ...periodsData.current,
              [MarketGraphChartDataGroupingPeriods.hour2]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.hour2],
                oddsData: newHours2LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.min5]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
                oddsData: newMin5LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.sec15]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
                oddsData: newSec15LineChartData
              }
            };
          } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.min5) {
            const newMin5LineChartData =
              currentGroupingPeriod === MarketGraphChartDataGroupingPeriods.min5
                ? newLineChartData
                : convertLineChartDataForSelection({
                    data: periodsData.current[MarketGraphChartDataGroupingPeriods.min5].oddsData
                  });
            const newSec15LineChartData =
              currentGroupingPeriod === MarketGraphChartDataGroupingPeriods.sec15
                ? newLineChartData
                : convertLineChartDataForSelection({
                    data: periodsData.current[MarketGraphChartDataGroupingPeriods.sec15].oddsData
                  });

            periodsData.current = {
              ...periodsData.current,
              [MarketGraphChartDataGroupingPeriods.min5]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.min5],
                oddsData: newMin5LineChartData
              },
              [MarketGraphChartDataGroupingPeriods.sec15]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
                oddsData: newSec15LineChartData
              }
            };
          } else if (allTimePeriod.current === MarketGraphChartDataGroupingPeriods.sec15) {
            periodsData.current = {
              ...periodsData.current,
              [MarketGraphChartDataGroupingPeriods.sec15]: {
                ...periodsData.current[MarketGraphChartDataGroupingPeriods.sec15],
                oddsData: newLineChartData
              }
            };
          }
          // endregion update cached data

          try {
            isDesktopSelectionChanged.current = true;
            // Need to reset data before set the new one
            lineSeriesRef.current.setData([]);
            volumeSeriesRef.current.setData([]);
            lineSeriesRef.current.setData(newLineChartData);
            volumeSeriesRef.current.setData(periodsData.current[currentGroupingPeriod].volumeData);
          } catch (error: unknown) {
            setIsChartError(true);
            // eslint-disable-next-line no-console
            console.log('set line data on change desktop selection error', {
              error,
              chartSelection,
              newSelection,
              newLineChartData,
              chartData,
              periodsData: periodsData.current,
              allTimePeriod: allTimePeriod.current,
              currentInterval: currentInterval.current
            });
          }
        }
      }
    }
  };

  const handleClickChartSelection = () => {
    if (isTabletOrMobileDevice) {
      let searchStr = location.search;

      if (searchParams.has(MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME)) {
        searchParams.delete(MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME);
        searchStr = `?${searchParams.toString()}`;
      }

      navigate(
        `${MARKET_GRAPH_LINE_CHART_SELECTION_BASE_URL}/${marketId}/${selectionId}/${handicap}${searchStr}&${MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME}=${selectedFilter}`,
        { replace: true }
      );
    } else {
      setIsOpenedDropdown(prevState => !prevState);
    }
  };

  const getLeftLegendLabel = () => {
    if (isChanceView) {
      return t('marketGraph.labels.impliedChance');
    }

    if (isLineMarket && marketUnit) {
      return marketUnitTranslated;
    }

    return t('account.mybets.labels.odds');
  };

  const toolbarItems = useMemo(() => {
    if (isTabletOrMobileDevice) {
      return [];
    }

    return [
      {
        icon: 'biab_custom-icon-chevron-left-2',
        disabled: !isChartZoomed || isToolbarPanLeftDisabled,
        onClick: () => panChart('left'),
        id: 'pan-left'
      },
      { icon: 'biab_custom-icon-minus', disabled: !isChartZoomed, onClick: () => zoomChart(false), id: 'zoom-out' },
      { icon: 'biab_custom-icon-refresh', disabled: !isChartZoomed, onClick: resetChart, id: 'reset' },
      { icon: 'biab_custom-icon-plus', disabled: false, onClick: () => zoomChart(true), id: 'zoom-in' },
      {
        icon: 'biab_custom-icon-chevron-right-2',
        disabled: !isChartZoomed || isToolbarPanRightDisabled,
        onClick: () => panChart('right'),
        id: 'pan-right'
      }
    ];
  }, [isChartZoomed, isToolbarPanLeftDisabled, isToolbarPanRightDisabled]);

  /**
   * Sets up keyboard shortcuts for chart navigation
   *
   * This effect adds keyboard event handlers for desktop users:
   * - Ctrl/Cmd+R: Reset chart to initial state
   * - Arrow Left/Right: Pan chart left/right
   * - Ctrl/Cmd+Plus: Zoom in
   * - Ctrl/Cmd+Minus: Zoom out
   */
  useEffect(() => {
    if (!isTabletOrMobileDevice) {
      const keyboardHandler = (event: KeyboardEvent) => {
        if (
          (event.metaKey || event.ctrlKey || (event.metaKey && event.altKey) || (event.ctrlKey && event.altKey)) &&
          (event.key === 'r' || event.code === 'KeyR' || event.key === '®')
        ) {
          event.preventDefault();

          if (isChartZoomedRef.current) {
            resetChart();
          }
        }

        if (event.key === 'ArrowLeft') {
          if (isChartZoomedRef.current && !isToolbarPanLeftDisabledRef.current) {
            panChart('left');
          }
        } else if (event.key === 'ArrowRight') {
          if (isChartZoomedRef.current && !isToolbarPanRightDisabledRef.current) {
            panChart('right');
          }
        } else if (
          (event.ctrlKey || event.metaKey) &&
          (event.key === '+' || event.key === '=' || event.code === 'NumpadAdd')
        ) {
          event.preventDefault();
          zoomChart(true);
        } else if ((event.ctrlKey || event.metaKey) && (event.key === '-' || event.code === 'NumpadSubtract')) {
          event.preventDefault();
          if (isChartZoomedRef.current) {
            zoomChart(false);
          }
        }
      };

      window.addEventListener('keydown', keyboardHandler);

      return () => {
        window.removeEventListener('keydown', keyboardHandler);
      };
    }
  }, []);

  const { refArray } = useTabulation({
    isActive: isOpenedDropdown,
    onTab: () => {
      setIsOpenedDropdown(false);
    },
    onEsc: () => {
      setIsOpenedDropdown(false);

      if (dropdownBtnRef?.current) {
        dropdownBtnRef.current.focus();
      }
    }
  });

  return (
    <>
      {/* Time filter buttons (All time, Day, Hour) */}
      <div
        className={classNames(styles.filters__container, {
          [styles.filters__container__mobile]: isTabletOrMobileDevice
        })}
      >
        <div
          className={classNames(styles.filters, {
            [styles.filters__dark]: isDarkTheme,
            [styles.filters__fullWidth]: isLineMarket || isAmericanOdds
          })}
        >
          {MARKET_GRAPH_CHART_FILTERS.map(({ labelKey, value }, index) => {
            const isActive = selectedFilter === value;

            return (
              <button
                key={value}
                tabIndex={index + 1 + MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX}
                data-active={isActive}
                className={classNames(styles.filters__item, {
                  [styles.filters__item__mobile]: isTabletOrMobileDevice,
                  [styles.filters__item__active]: isActive,
                  [styles.filters__item__dark]: isDarkTheme,
                  [styles.filters__item__active__dark]: isActive && isDarkTheme
                })}
                onClick={() => handleSelectFilter(value)}
              >
                {t(labelKey)}
              </button>
            );
          })}
        </div>
        {/* Odds/Chance view toggle dropdown */}
        {!isAmericanOdds && !isLineMarket && (
          <div className={styles.toggle__container} ref={dropdownRef}>
            <button
              ref={dropdownBtnRef}
              tabIndex={MARKET_GRAPH_CHART_FILTERS.length + MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX + 1}
              onClick={handleClickChartSelection}
              data-open={isTabletOrMobileDevice ? undefined : isOpenedDropdown}
              className={classNames(styles.toggle, {
                [styles.toggle__mobile]: isTabletOrMobileDevice,
                [styles.toggle__opened]: isOpenedDropdown,
                [styles.toggle__dark]: isDarkTheme,
                [styles.toggle__opened__dark]: isOpenedDropdown && isDarkTheme
              })}
            >
              <i
                className={classNames('biab_custom-icon-chart', marketGraphsBranding.ICON, styles.toggle__icon, {
                  [styles.toggle__icon__mobile]: isTabletOrMobileDevice,
                  [styles.toggle__icon__dark]: isDarkTheme
                })}
              />
            </button>
            {!isTabletOrMobileDevice && (
              <div
                className={classNames(styles.dropdown, {
                  [styles.dropdown__opened]: isOpenedDropdown,
                  [styles.dropdown__closed]: !isOpenedDropdown,
                  [styles.dropdown__dark]: isDarkTheme
                })}
              >
                {chartSelections.map(({ label, value }, index) => {
                  const isSelected = value === chartSelection;

                  return (
                    <button
                      key={value}
                      tabIndex={
                        isOpenedDropdown
                          ? MARKET_GRAPH_CHART_FILTERS.length + MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX + 2 + index
                          : -1
                      }
                      className={classNames(styles.dropdown__item, {
                        [styles.dropdown__item__selected]: isSelected,
                        [styles.dropdown__item__dark]: isDarkTheme
                      })}
                      onClick={() => handleChangeDesktopSelection(value)}
                      ref={(element: HTMLButtonElement) => (refArray.current[index] = element)}
                    >
                      {label}
                      {isSelected && (
                        <i
                          className={classNames('biab_custom-icon-check', styles.dropdown__item__icon, {
                            [styles.dropdown__item__icon__dark]: isDarkTheme
                          })}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Chart area with legend, visualization, and controls */}
      <div
        className={classNames(styles.legendAndChart, {
          [styles.legendAndChart__mobile]: isTabletOrMobileDevice
        })}
      >
        {/* Chart legend showing data series types */}
        <div className={styles.legend}>
          <div className={styles.legend__block}>
            <p
              className={classNames(styles.legend__text, {
                [styles.legend__text__mobile]: isTabletOrMobileDevice,
                [styles.legend__text__dark]: isDarkTheme
              })}
            >
              {getLeftLegendLabel()}
            </p>
            <div
              id="odds-legend-color"
              className={classNames(styles.legend__line, marketGraphsBranding.LINE_CHART, {
                [styles.legend__line__dark]: isDarkTheme
              })}
              ref={oddsLegendColorRef}
            />
          </div>
          <div className={styles.legend__block}>
            <div
              id="volume-legend-color"
              className={classNames(styles.legend__square, marketGraphsBranding.VOLUME_BAR, {
                [styles.legend__square__dark]: isDarkTheme
              })}
              ref={volumeLegendColorRef}
            />
            <p
              className={classNames(styles.legend__text, {
                [styles.legend__text__mobile]: isTabletOrMobileDevice,
                [styles.legend__text__dark]: isDarkTheme
              })}
            >
              {t('marketGraph.labels.volume')}
            </p>
          </div>
        </div>
        {/* Main chart container with visualization and interactive elements */}
        <div
          className={classNames(styles.chartContainer, {
            [styles.chartContainer__simplified__mobile]: isMobile && isSimplifiedView
          })}
        >
          {/* Chart visualization area where the actual chart is rendered */}
          <div
            ref={chartContainerRef}
            className={classNames(styles.chart, {
              [styles.chart__mobile]: isTabletOrMobileDevice,
              [styles.chart__smallScreenView]: isSmallScreenView && !isTabletOrMobileDevice,
              [styles.chart__simplified]: !isMobile && isSimplifiedView,
              [styles.chart__simplified__mobile]: isMobile && isSimplifiedView
            })}
            style={
              isMobile
                ? undefined
                : {
                    maxHeight: `${
                      minWindowHeight - headerHeight - totalMatchedBlockHeight - DESKTOP_STATIC_ELEMENTS_HEIGHT
                    }px`
                  }
            }
          >
            {/* In-play marker showing when the market went in-play */}
            <p
              ref={inPlayMarkerTextRef}
              className={classNames(styles.chart__inPlayMarker__text, marketGraphsBranding.IN_PLAY_INDICATOR, {
                hidden: !marketStartTime || !isMarketInPlay,
                [styles.chart__inPlayMarker__text__mobile]: isTabletOrMobileDevice,
                [styles.chart__inPlayMarker__text__dark]: isDarkTheme
              })}
            >
              {t('market.inPlay')}
            </p>
            <div
              ref={inPlayMarkerLineRef}
              className={classNames(styles.chart__inPlayMarker__line, marketGraphsBranding.IN_PLAY_INDICATOR, {
                hidden: !marketStartTime || !isMarketInPlay,
                [styles.chart__inPlayMarker__line__dark]: isDarkTheme
              })}
            />
          </div>
          {/* Tooltip showing detailed information on hover */}
          <div
            className={classNames(styles.tooltip, {
              [styles.tooltip__dark]: isDarkTheme
            })}
            ref={tooltipRef}
          >
            <p
              className={classNames(styles.tooltip__text, {
                [styles.tooltip__text__dark]: isDarkTheme,
                [styles.tooltip__text__mobile]: isTabletOrMobileDevice
              })}
              ref={tooltipDateRef}
            />
            <div
              className={classNames(styles.tooltip__block, {
                [styles.tooltip__block__mobile]: isTabletOrMobileDevice
              })}
            >
              <p
                className={classNames(styles.tooltip__text, {
                  [styles.tooltip__text__dark]: isDarkTheme,
                  [styles.tooltip__text__mobile]: isTabletOrMobileDevice
                })}
              >
                {getLeftLegendLabel()}:
              </p>
              <p
                className={classNames(styles.tooltip__value, {
                  [styles.tooltip__value__dark]: isDarkTheme,
                  [styles.tooltip__value__mobile]: isTabletOrMobileDevice
                })}
                ref={tooltipOddsValueRef}
              />
            </div>
            <div
              className={classNames(styles.tooltip__block, {
                [styles.tooltip__block__mobile]: isTabletOrMobileDevice
              })}
            >
              <p
                className={classNames(styles.tooltip__text, {
                  [styles.tooltip__text__dark]: isDarkTheme,
                  [styles.tooltip__text__mobile]: isTabletOrMobileDevice
                })}
              >
                {t('marketGraph.labels.volume')}:
              </p>
              <p
                className={classNames(styles.tooltip__value, {
                  [styles.tooltip__value__dark]: isDarkTheme,
                  [styles.tooltip__value__mobile]: isTabletOrMobileDevice
                })}
                ref={tooltipVolumeValueRef}
              />
            </div>
          </div>
          {/* Chart toolbar with zoom and pan controls (desktop only) */}
          {!isMobile && (
            <div className={classNames(styles.toolbar, { [styles.toolbar__dark]: isDarkTheme })}>
              {toolbarItems.map(({ icon, id, onClick, disabled }, index) => {
                return (
                  <button
                    key={id}
                    tabIndex={index + 2 + MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX + MARKET_GRAPH_CHART_FILTERS.length}
                    data-action={id}
                    className={classNames(styles.toolbar__item, { [styles.toolbar__item__dark]: isDarkTheme })}
                    disabled={disabled}
                    onClick={onClick}
                  >
                    <i
                      className={classNames(icon, styles.toolbar__item__icon, {
                        [styles.toolbar__item__icon__disabled]: disabled,
                        [styles.toolbar__item__icon__dark]: isDarkTheme,
                        [styles.toolbar__item__icon__disabled__dark]: isDarkTheme && disabled
                      })}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
